import { useState, useCallback } from 'react';

/**
 * useTableSort - Reusable hook for table sorting
 * 
 * Handles sort state and provides sorting function for tracking dashboards.
 * 
 * @param {Object} options
 * @param {Array} options.columns - Column definitions with { key, type }
 * @returns {Object} Sort state and actions
 */
const useTableSort = ({ columns = [] }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  /**
   * Handle column sort click - cycles through: asc -> desc -> none
   */
  const handleSort = useCallback((columnKey) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // Clear sort
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  /**
   * Set sort directly (for programmatic control)
   */
  const setSort = useCallback((column, direction = 'asc') => {
    setSortColumn(column);
    setSortDirection(direction);
  }, []);

  /**
   * Clear sort
   */
  const clearSort = useCallback(() => {
    setSortColumn(null);
    setSortDirection('asc');
  }, []);

  /**
   * Apply sorting to items
   * @param {Array} items - Items to sort
   * @returns {Array} Sorted items
   */
  const applySorting = useCallback((items) => {
    if (!sortColumn) return items;

    const result = [...items];
    const col = columns.find(c => c.key === sortColumn);

    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle nulls - always push to end
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Numeric comparison for number columns
      if (col?.type === 'number') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Date comparison
      if (col?.type === 'date') {
        const dateA = new Date(aVal);
        const dateB = new Date(bVal);
        if (!isNaN(dateA) && !isNaN(dateB)) {
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }
      }

      // String comparison (default)
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [sortColumn, sortDirection, columns]);

  return {
    // State
    sortColumn,
    sortDirection,

    // Actions
    handleSort,
    setSort,
    clearSort,

    // Sort function
    applySorting,
  };
};

export default useTableSort;
